// =====================================================================
// FloatingSystemStatus — replaces the old always-on "System Online" line
// that used to live pinned in the sidebar footer. Polls the four backend
// services' /health endpoints (healthAPI.getSystemStatus, services/api.js)
// and renders as a small fixed-position widget:
//   - healthy  → a minimized dot pill (quiet, not a persistent claim)
//   - degraded → auto-expands into a card naming exactly which service(s)
//                are down, so "something's wrong" is never a silent state
//   - loading  → a neutral pill while the first poll is in flight
//
// The overall/per-service classification is pure logic in
// utils/systemStatusLogic.mjs (test:logic-covered) — this component is only
// the polling + open/closed UI wiring around it.
// =====================================================================

import React, { useEffect, useRef, useState } from 'react'
import { AlertTriangle, CheckCircle2, ChevronDown, Loader2, XCircle } from 'lucide-react'
import { healthAPI } from '../services/api'
import { cn } from '../utils/cn'
import { computeOverallStatus, serviceRows } from '../utils/systemStatusLogic.mjs'
import { usePrefersReducedMotion } from '../hooks/usePrefersReducedMotion'

const POLL_INTERVAL_MS = 30000

const STATUS_DOT = {
  loading: 'bg-gray-300',
  healthy: 'bg-success-500',
  degraded: 'bg-danger-500',
}

const FloatingSystemStatus = () => {
  const [statusData, setStatusData] = useState(null)
  const [loading, setLoading] = useState(true)
  // null = follow the automatic open/closed rule below; true/false = the
  // user explicitly toggled it, which wins until the overall status itself
  // changes (a fresh transition always clears the override, so a newly
  // degraded service can never stay silently collapsed just because the
  // user dismissed an earlier, unrelated card).
  const [manualOpen, setManualOpen] = useState(null)
  const prevOverallRef = useRef('loading')
  const reduceMotion = usePrefersReducedMotion()

  useEffect(() => {
    let active = true
    const poll = () => {
      healthAPI
        .getSystemStatus()
        .then((data) => {
          if (!active) return
          setStatusData(data)
          setLoading(false)
        })
        .catch(() => {
          if (!active) return
          setStatusData(null)
          setLoading(false)
        })
    }
    poll()
    const id = setInterval(poll, POLL_INTERVAL_MS)
    return () => {
      active = false
      clearInterval(id)
    }
  }, [])

  const overall = computeOverallStatus(statusData, loading)
  const rows = serviceRows(statusData)
  const downRows = rows.filter((r) => !r.healthy)

  useEffect(() => {
    if (overall !== prevOverallRef.current) {
      setManualOpen(null)
      prevOverallRef.current = overall
    }
  }, [overall])

  const isOpen = manualOpen !== null ? manualOpen : overall === 'degraded'
  const toggle = () => setManualOpen(!isOpen)

  const Icon = overall === 'healthy' ? CheckCircle2 : overall === 'degraded' ? AlertTriangle : Loader2
  const label =
    overall === 'loading'
      ? 'Checking system status…'
      : overall === 'healthy'
        ? 'All systems operational'
        : `${downRows.length} service${downRows.length === 1 ? '' : 's'} degraded`

  // z-30, deliberately below Sidebar's mobile scrim/drawer (z-40/z-50) and
  // GlobalSearch's modal (z-[60]) — when either of those is open, this
  // floating widget should sit under them, not float on top.
  return (
    <div className="fixed bottom-4 right-4 z-30 sm:bottom-5 sm:right-5" role="status" aria-live="polite">
      {isOpen && (
        <div className="mb-2 w-72 overflow-hidden rounded-2xl border border-gray-200/80 bg-white shadow-elev-2">
          <div className="flex items-center justify-between gap-2 border-b border-gray-100 px-4 py-3">
            <div className="flex items-center gap-2 min-w-0">
              <Icon
                className={cn(
                  'h-4 w-4 shrink-0',
                  overall === 'healthy' && 'text-success-600',
                  overall === 'degraded' && 'text-danger-600',
                  overall === 'loading' && cn('text-gray-400', !reduceMotion && 'animate-spin')
                )}
                strokeWidth={1.75}
              />
              <span className="truncate text-sm font-semibold text-gray-900">{label}</span>
            </div>
            <button
              type="button"
              onClick={toggle}
              aria-label="Collapse system status"
              className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-600 focus-visible:ring-offset-2"
            >
              <ChevronDown className="h-4 w-4" strokeWidth={1.75} />
            </button>
          </div>
          <ul className="divide-y divide-gray-100">
            {rows.map((row) => (
              <li key={row.key} className="flex items-center justify-between gap-2 px-4 py-2.5">
                <span className="text-sm text-gray-700">{row.label}</span>
                <span
                  className={cn(
                    'inline-flex items-center gap-1.5 text-xs font-medium',
                    row.healthy ? 'text-success-700' : 'text-danger-700'
                  )}
                >
                  {row.healthy ? (
                    <CheckCircle2 className="h-3.5 w-3.5" strokeWidth={1.75} />
                  ) : (
                    <XCircle className="h-3.5 w-3.5" strokeWidth={1.75} />
                  )}
                  {row.healthy ? 'Healthy' : row.status === 'unknown' ? 'Unknown' : 'Down'}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <button
        type="button"
        onClick={toggle}
        aria-expanded={isOpen}
        aria-label={`System status: ${label}. ${isOpen ? 'Collapse' : 'Expand'} details.`}
        title={label}
        className="flex items-center gap-2 rounded-full border border-gray-200/70 bg-white pl-2.5 pr-3.5 py-1.5 shadow-elev-1 transition-all hover:border-gray-300 hover:shadow-elev-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-600 focus-visible:ring-offset-2"
      >
        <span className="relative flex h-2 w-2 shrink-0">
          {overall === 'degraded' && (
            <span
              className={cn(
                'absolute inline-flex h-full w-full rounded-full bg-danger-400',
                !reduceMotion && 'animate-pulse-soft'
              )}
            />
          )}
          <span className={cn('relative inline-flex h-2 w-2 rounded-full', STATUS_DOT[overall])} />
        </span>
        <span className="text-xs font-medium text-gray-600 max-w-[9rem] truncate">
          {overall === 'healthy' ? 'Operational' : overall === 'loading' ? 'Checking…' : label}
        </span>
      </button>
    </div>
  )
}

export default FloatingSystemStatus
